import { useCallback } from "react";

import { useScrollGroupContext } from "./context";
import { useScrollGroupSetScroll } from "./useScrollGroupSetScroll";

/**
 * Programmatically scroll all items in the group. The arguments of the returned function is similar
 * to element.scrollTo(...args)
 */
export function useScrollGroupTo() {
  const { scrollGroupItemsRef, currentManualScrollMetaRef } =
    useScrollGroupContext();

  const setGroupScroll = useScrollGroupSetScroll();

  return useCallback(
    (offset: { top?: number; left?: number; behavior?: "smooth" }) => {
      // Set a flag that we're handling the scroll programmatically for a while
      currentManualScrollMetaRef.current = { target: offset };

      const markScrollAsComplete = () => {
        if (currentManualScrollMetaRef.current?.target === offset) {
          currentManualScrollMetaRef.current = null;

          // And make sure all elements are synced again
          const firstElement =
            scrollGroupItemsRef.current?.[0]?.refObject?.current;
          if (firstElement) {
            setGroupScroll({
              top: firstElement.scrollTop,
              left: firstElement.scrollLeft,
            });
          }
        }
      };

      // Scroll all elements
      scrollGroupItemsRef.current?.forEach((item) => {
        const element = item.refObject.current;
        if (element) {
          element.scrollTo(offset);
        }
      });

      // Clear the flag that we're handling the scroll programmatically after some time, as a fallback
      setTimeout(() => {
        markScrollAsComplete();
      }, 400);
    },
    [currentManualScrollMetaRef, scrollGroupItemsRef, setGroupScroll],
  );
}
